import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { ConditionBuilder } from '../derivations/ConditionBuilder'
import { labelIn, type ScenarioVocabulary } from '../../hooks/useScenariosApi'
import { CodeMultiPicker, type CodeOption } from './CodeMultiPicker'
import type { EditorOptions } from './editor-options'

type Params = Record<string, unknown>

/** The special codes a scenario may put in a list (lint.go FromRule / FromTicket). */
const FROM_RULE = 'from_rule'
const FROM_TICKET = 'from_ticket'

const SELECT = 'h-8 rounded border border-border bg-background px-2 text-xs'

function strings(v: unknown): string[] {
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

/** Drops the keys left empty so the saved JSON stays minimal. */
function compact(p: Params): Params {
    const out: Params = {}
    for (const [k, v] of Object.entries(p)) {
        if (v === undefined || v === '' || v === false || (Array.isArray(v) && v.length === 0)) continue
        out[k] = v
    }
    return out
}

/**
 * The raw JSON of a parameter object, kept as an escape hatch below the
 * pickers (composite proofs, parameters no picker covers).
 */
function AdvancedJson({ value, onChange, testId }: { value: Params | undefined; onChange: (p: Params) => void; testId: string }) {
    const { t } = useTranslation('scenarios')
    const serialised = JSON.stringify(value ?? {})
    const [text, setText] = useState(serialised)
    const [invalid, setInvalid] = useState(false)
    useEffect(() => {
        setText(serialised)
        setInvalid(false)
    }, [serialised])
    return (
        <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">{t('editor.advanced_json')}</summary>
            <Input
                value={text}
                onChange={(e) => {
                    setText(e.target.value)
                    try {
                        const parsed = e.target.value.trim() ? JSON.parse(e.target.value) : {}
                        setInvalid(false)
                        onChange(parsed)
                    } catch {
                        setInvalid(true)
                    }
                }}
                aria-invalid={invalid}
                className="mt-1 font-mono text-xs"
                data-testid={testId}
            />
        </details>
    )
}

function StepSelect({ value, steps, onChange, emptyLabel, testId }: { value: string; steps: string[]; onChange: (v: string) => void; emptyLabel: string; testId: string }) {
    return (
        <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
            <option value="">{emptyLabel}</option>
            {[...steps, ...(value && !steps.includes(value) ? [value] : [])].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
    )
}

/** The parameters of a step's recipient resolver, as pickers (spec 2026-10-28-15 § 4). */
export function RecipientParamsEditor({ resolver, params, onChange, vocab, options, steps, index }: {
    resolver: string
    params: Params | undefined
    onChange: (p: Params) => void
    vocab: ScenarioVocabulary | undefined
    options: EditorOptions
    steps: string[]
    index: number
}) {
    const { t, i18n } = useTranslation('scenarios')
    const p = params ?? {}
    const set = (patch: Params) => onChange(compact({ ...p, ...patch }))
    const services = (vocab?.services ?? []).filter((s) => (resolver === 'public_body' ? s.family === 'public' : s.family === 'provider'))
    const contractTypes: CodeOption[] = [{ code: FROM_TICKET, label: t('editor.from_ticket') }, ...options.contractTypes]
    return (
        <div className="space-y-1" data-testid={`scenario-step-resolver-params-${index}`}>
            {(resolver === 'providers_by_service' || resolver === 'public_body') && (
                <label className="flex items-center gap-2 text-xs">
                    {t('editor.service')}
                    <select className={SELECT} value={String(p.service ?? '')} onChange={(e) => set({ service: e.target.value })} data-testid={`scenario-step-service-${index}`}>
                        <option value="">{resolver === 'providers_by_service' ? t('editor.from_rule') : t('editor.none')}</option>
                        {services.map((s) => <option key={s.code} value={s.code}>{labelIn(s.label, i18n.language, t(`services.${s.code}`, { defaultValue: s.code }))}</option>)}
                    </select>
                </label>
            )}
            {(resolver === 'occupants_of_scope' || resolver === 'co_owners_of_scope') && (
                <label className="flex items-center gap-2 text-xs">
                    {t('editor.scope')}
                    <select className={SELECT} value={String(p.scope ?? 'building')} onChange={(e) => set({ scope: e.target.value === 'building' ? undefined : e.target.value })} data-testid={`scenario-step-scope-${index}`}>
                        <option value="building">{t('editor.scope_options.building')}</option>
                        <option value="target">{t('editor.scope_options.target')}</option>
                    </select>
                </label>
            )}
            {resolver === 'contract_providers_by_type' && (
                <>
                    <CodeMultiPicker value={strings(p.contract_types)} options={contractTypes} onChange={(v) => set({ contract_types: v })} placeholder={t('editor.contract_types')} testId={`scenario-step-contract-types-${index}`} />
                    <label className="flex items-center gap-2 text-xs">
                        {t('editor.exclude_declined')}
                        <StepSelect value={String(p.exclude_declined ?? '')} steps={steps} onChange={(v) => set({ exclude_declined: v })} emptyLabel={t('editor.none')} testId={`scenario-step-exclude-declined-${index}`} />
                    </label>
                </>
            )}
            <AdvancedJson value={params} onChange={onChange} testId={`scenario-step-resolver-json-${index}`} />
        </div>
    )
}

/** The parameters of a step's proof, as pickers (spec 2026-10-28-15 § 6). */
export function ProofParamsEditor({ proof, onChange, options, steps, org, index }: {
    proof: (Params & { kind?: string }) | undefined
    onChange: (p: Params & { kind?: string }) => void
    options: EditorOptions
    steps: string[]
    org: string
    index: number
}) {
    const { t } = useTranslation('scenarios')
    if (!proof?.kind) return null
    const p = proof
    const set = (patch: Params) => onChange({ ...compact({ ...p, ...patch }), kind: p.kind })
    const docTypes: CodeOption[] = [{ code: FROM_RULE, label: t('editor.from_rule') }, ...options.documentTypes]
    const contractTypes: CodeOption[] = [{ code: FROM_TICKET, label: t('editor.from_ticket') }, ...options.contractTypes]
    const id = (name: string) => `scenario-step-proof-${name}-${index}`
    const onSelect = (values: string[]) => (
        <label className="flex items-center gap-2 text-xs">
            {t('editor.proof_on')}
            <select className={SELECT} value={String(p.on ?? values[0])} onChange={(e) => set({ on: e.target.value === values[0] ? undefined : e.target.value })} data-testid={id('on')}>
                {values.map((v) => <option key={v} value={v}>{t(`editor.proof_on_options.${v}`)}</option>)}
            </select>
        </label>
    )
    return (
        <div className="space-y-1 md:col-span-2" data-testid={id('editor')}>
            {p.kind === 'document_linked' && (
                <>
                    <CodeMultiPicker value={strings(p.document_types)} options={docTypes} onChange={(v) => set({ document_types: v })} placeholder={t('editor.document_types')} testId={id('document-types')} />
                    <div className="flex flex-wrap items-center gap-3">
                        {onSelect(['target', 'dossier', 'step', 'intervention', 'contract'])}
                        <label className="flex items-center gap-2 text-xs">
                            {t('editor.min_count')}
                            <Input type="number" min={1} className="h-8 w-16 text-xs" value={Number(p.min_count ?? 1)} onChange={(e) => set({ min_count: Number(e.target.value) > 1 ? Number(e.target.value) : undefined })} data-testid={id('min-count')} />
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                            <Checkbox checked={!!p.valid} onCheckedChange={(c) => set({ valid: c === true })} data-testid={id('valid')} />
                            {t('editor.valid')}
                        </label>
                    </div>
                </>
            )}
            {(p.kind === 'intervention_status' || p.kind === 'intervention_validated') && (
                <div className="flex flex-wrap items-center gap-3">
                    {p.kind === 'intervention_status' && (
                        <label className="flex items-center gap-2 text-xs">
                            {t('editor.status')}
                            <select className={SELECT} value={String(p.status ?? 'done')} onChange={(e) => set({ status: e.target.value })} data-testid={id('status')}>
                                <option value="submitted">{t('editor.status_options.submitted')}</option>
                                <option value="done">{t('editor.status_options.done')}</option>
                            </select>
                        </label>
                    )}
                    <label className="flex items-center gap-2 text-xs">
                        {t('editor.step')}
                        <StepSelect value={String(p.step ?? '')} steps={steps} onChange={(v) => set({ step: v })} emptyLabel={t('editor.this_step')} testId={id('step')} />
                    </label>
                </div>
            )}
            {(p.kind === 'step_done' || p.kind === 'decision_recorded') && (
                <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs">
                        {t('editor.step')}
                        <StepSelect value={String(p.step ?? '')} steps={steps} onChange={(v) => set({ step: v })} emptyLabel={t('editor.this_step')} testId={id('step')} />
                    </label>
                    {p.kind === 'decision_recorded' && (
                        <label className="flex items-center gap-2 text-xs">
                            {t('editor.option')}
                            <Input className="h-8 w-40 text-xs" value={String(p.option ?? '')} placeholder={t('editor.any_option')} onChange={(e) => set({ option: e.target.value })} data-testid={id('option')} />
                        </label>
                    )}
                </div>
            )}
            {p.kind === 'compliance_resolved' && (
                <CodeMultiPicker
                    single
                    value={p.rule_code ? [String(p.rule_code)] : []}
                    options={options.rules}
                    onChange={(v) => set({ rule_code: v[0] })}
                    placeholder={`${t('editor.rule')} (${t('editor.dossier_rule')})`}
                    testId={id('rule')}
                />
            )}
            {p.kind === 'contract_linked' && (
                <>
                    <CodeMultiPicker value={strings(p.contract_types)} options={contractTypes} onChange={(v) => set({ contract_types: v })} placeholder={t('editor.contract_types')} testId={id('contract-types')} />
                    <label className="flex items-center gap-2 text-xs">
                        <Checkbox checked={!!p.in_force} onCheckedChange={(c) => set({ in_force: c === true })} data-testid={id('in-force')} />
                        {t('editor.in_force')}
                    </label>
                </>
            )}
            {p.kind === 'contract_changed' && (
                <div className="flex flex-wrap items-center gap-3" data-testid={id('changes')}>
                    <span className="text-xs text-muted-foreground">{t('editor.changes')}</span>
                    {['amended', 'renewed', 'notice_recorded', 'ended'].map((c) => {
                        const list = strings(p.changes)
                        return (
                            <label key={c} className="flex items-center gap-1 text-xs">
                                <Checkbox
                                    checked={list.includes(c)}
                                    onCheckedChange={(on) => set({ changes: on === true ? [...list, c] : list.filter((x) => x !== c) })}
                                    data-testid={id(`change-${c}`)}
                                />
                                {t(`editor.change_options.${c}`)}
                            </label>
                        )
                    })}
                </div>
            )}
            {p.kind === 'meter_reading_recorded' && (
                <label className="flex items-center gap-2 text-xs">
                    <Checkbox checked={!!p.with_photo} onCheckedChange={(c) => set({ with_photo: c === true })} data-testid={id('with-photo')} />
                    {t('editor.with_photo')}
                </label>
            )}
            {p.kind === 'property_holds' && (
                <>
                    {onSelect(['target', 'contract'])}
                    <ConditionBuilder
                        conditions={p.condition ?? { all: [] }}
                        onChange={(condition) => set({ condition })}
                        org={org}
                        defaultDateMode="relative"
                    />
                </>
            )}
            <AdvancedJson value={proof} onChange={(v) => onChange(v as Params & { kind?: string })} testId={`scenario-step-proof-params-${index}`} />
        </div>
    )
}
